import DefaultLayout from "layouts/Default";
import LocationLayout from "layouts/LocationLayout";
import {wrapper} from "lib/store";
import {getLocation} from "lib/features/locationSlice";
import {getLocationPaginatedReviews, getLocationReviewStats} from "lib/services/review";
import Wrapper from "components/blocks/Wrapper";
import Container from "components/blocks/Container";
import {CircularProgress, LinearProgress} from "@mui/material";
import {useSelector} from "react-redux";
import Rating from "components/Rating";
import ReviewCard from "components/cards/ReviewCard";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {useTranslation} from "next-i18next";

const SubRating = ({label, rating}) => {
  return (
    <div className="w-[50px] h-[50px] relative">
      <CircularProgress
        color="primary"
        size={50}
        variant="determinate"
        value={rating/5*100}
        className={"absolute inset-0"}
      />
      <div className="w-full h-full p-2 flex flex-col items-center justify-center text-sm">
        {rating}
      </div>
      <div className="absolute top-full left-1/2 pt-1 text-xs inline-block -translate-x-1/2">
        {label}
      </div>
    </div>
  )
}

const RatingBreakdown = ({label, total, count}) => {
  return (
    <div className="flex items-center mb-1">
      <label className="shrink-0 w-[80px] text-gray-500 text-sm">{label}</label>
      <LinearProgress
        variant="determinate"
        value={count / total * 100}
        className={"grow"}
        classes={{
          root: "h-2",
        }}
      />
      <label className="shrink-0 w-[50px] text-right text-gray-500 text-sm">{count}</label>
    </div>
  )
}

export const getServerSideProps = wrapper.getServerSideProps(store => async (context) => {
  const {
    query,locale,
  } = context
  await store.dispatch(getLocation({slug: query.locationSlug, id: query.id}))
  const reviews = await getLocationPaginatedReviews()
  const stats = await getLocationReviewStats()

  const {
    page=1
  } = query

  return {
    props: {
      page,
      reviews,
      stats,
      ...(await serverSideTranslations(locale, ['home', 'common']))
    }
  }
})

export default function Reviews(props) {
  const {
    page, reviews, stats
  } = props

  const location = useSelector(state => state.location.location)
  const { t } = useTranslation()

  return (
    <Wrapper>
      <Container className="max-w-screen-sm">
        <div className="flex justify-between">
        </div>
        <div className="flex mb-4">
          <div className="w-[100px] mr-8">
            <div className="w-[100px] h-[100px] relative mb-2">
              <CircularProgress
                color="primary"
                size={100}
                variant="determinate"
                value={location.rating/5*100}
                className={"absolute inset-0"}
              />
              <div className="w-full h-full p-2 flex flex-col items-center justify-center">
                <div className="text-2xl leading-none">
                  {location.rating.toFixed(1)}
                </div>
                <Rating
                  value={location.rating || 5}
                  precision={0.5}
                  readOnly
                  className={'mt-2 text-xs'}
                />
              </div>
            </div>
            <div className="text-sm text-gray-500 text-center">
              {`${stats.reviews_count} ${t('review')}`}
            </div>
          </div>
          <div className="grow">
            <RatingBreakdown label="5 Stars" count={stats.star_5_count} total={stats.reviews_count}/>
            <RatingBreakdown label="4 Stars" count={stats.star_4_count} total={stats.reviews_count}/>
            <RatingBreakdown label="3 Stars" count={stats.star_3_count} total={stats.reviews_count}/>
            <RatingBreakdown label="2 Stars" count={stats.star_2_count} total={stats.reviews_count}/>
            <RatingBreakdown label="1 Star" count={stats.star_1_count} total={stats.reviews_count}/>
          </div>
        </div>
        <div className={"flex gap-8 pb-5"}>
          <SubRating label={t('home:review_quality')} rating={stats.quality_rating}/>
          <SubRating label={t('home:review_service')} rating={stats.service_rating}/>
          <SubRating label={t('home:review_atmosphere')} rating={stats.atmosphere_rating}/>
        </div>
        <div className="mb-8"></div>
        {
          reviews.map(review => (
            <ReviewCard key={review.id} review={review} location={location}/>
          ))
        }
      </Container>
    </Wrapper>
  )
}

Reviews.getLayout = function getLayout(page) {
  return (
    <DefaultLayout>
      <LocationLayout>
        {page}
      </LocationLayout>
    </DefaultLayout>
  )
}
